import { HumanTextbookJSON } from './HumanTextbookSchema';
import { GeometryEngine } from '../core/GeometryEngine';
import { GeometryLayoutSolver } from '../solver/GeometryLayoutSolver';
import { ConstructionStep } from '../types';
import { Point } from '../objects/Point';
import { Ray } from '../objects/Ray';
import { Segment } from '../objects/Segment';
import { Circle } from '../objects/Circle';
import { Arc } from '../objects/Arc';
import { Angle } from '../objects/Angle';
import { Label } from '../objects/Label';
import { DrawPoint, DrawLine, DrawArc, DrawCircle } from '../animations/DrawPoint';
import { Highlight, Fade } from '../animations/MoveCompass';
import { SpeechManager } from '../utils/SpeechManager';

export class HumanTextbookParser {
  public static parse(json: HumanTextbookJSON, engine: GeometryEngine): { steps: ConstructionStep[]; justification?: string } {
    const solver = new GeometryLayoutSolver();
    const steps: ConstructionStep[] = [];
    const createdObjects = new Map<string, any>();
    const arcKeys: string[] = [];

    // Optional Introduction Step
    if (json.introduction) {
      const estIntroMs = SpeechManager.getInstance().estimateDurationMs(json.introduction);
      const introStep: ConstructionStep = {
        id: 'step_intro',
        title: 'Lesson Introduction',
        description: json.title,
        narration: json.introduction,
        duration: estIntroMs,
        animations: [],
      };
      if (json.introVoiceoverAudioUrl) {
        (introStep as any).voiceoverAudioUrl = json.introVoiceoverAudioUrl;
      }
      steps.push(introStep);
    }

    json.steps.forEach((tbStep, idx) => {
      const actionsList = (tbStep as any).actions || ((tbStep as any).action ? [(tbStep as any).action] : []);
      const animations: any[] = [];

      actionsList.forEach((act: any) => {
        switch (act.draw) {
          case 'ray': {
            const startName = act.startPoint || 'A';
            const passName = act.passPoint || 'B';

            let angleRad = 0;
            if (solver.hasPoint(passName)) {
              const pS = solver.getPoint(startName);
              const pP = solver.getPoint(passName);
              angleRad = Math.atan2(pP.y - pS.y, pP.x - pS.x);
            } else if (passName.toUpperCase() === 'E' || passName.toUpperCase() === 'C') {
              angleRad = -Math.PI / 3; // 60 deg UPWARDS
            } else if (passName.toUpperCase() === 'P4') {
              angleRad = -Math.PI / 2; // 90 deg UPWARDS
            } else if (passName.toUpperCase() === 'R') {
              angleRad = -Math.PI / 6; // 30 deg UPWARDS
            }

            const { pStart, pPass } = solver.solveRay(startName, passName, angleRad);

            let pStartObj = createdObjects.get(startName);
            if (!pStartObj) {
              pStartObj = new Point(engine.renderer, pStart.x, pStart.y, startName, `pt_${startName}`);
              pStartObj.setVisible(false);
              createdObjects.set(startName, pStartObj);
              animations.push(new DrawPoint(pStartObj));
            }

            let pPassObj = createdObjects.get(passName);
            if (!pPassObj) {
              pPassObj = new Point(engine.renderer, pPass.x, pPass.y, passName, `pt_${passName}`);
              pPassObj.setVisible(false);
              createdObjects.set(passName, pPassObj);
              animations.push(new DrawPoint(pPassObj));
            }

            const rayObj = new Ray(engine.renderer, pStart, pPass, `ray_${startName}${passName}`);
            rayObj.setVisible(false);
            createdObjects.set(`ray_${startName}${passName}`, rayObj);
            animations.push(new DrawLine(rayObj, engine.ruler));
            break;
          }

          case 'segment':
          case 'line': {
            const startName = act.startPoint || 'A';
            const endName = act.endPoint || act.passPoint || 'B';

            const { pStart, pEnd } = Array.isArray(act.from) && Array.isArray(act.to)
              ? { pStart: { x: act.from[0], y: act.from[1] }, pEnd: { x: act.to[0], y: act.to[1] } }
              : solver.solveSegment(startName, endName);

            let pStartObj = createdObjects.get(startName);
            if (!pStartObj && !Array.isArray(act.from)) {
              pStartObj = new Point(engine.renderer, pStart.x, pStart.y, startName, `pt_${startName}`);
              pStartObj.setVisible(false);
              createdObjects.set(startName, pStartObj);
              animations.push(new DrawPoint(pStartObj));
            }

            let pEndObj = createdObjects.get(endName);
            if (!pEndObj && !Array.isArray(act.to)) {
              pEndObj = new Point(engine.renderer, pEnd.x, pEnd.y, endName, `pt_${endName}`);
              pEndObj.setVisible(false);
              createdObjects.set(endName, pEndObj);
              animations.push(new DrawPoint(pEndObj));
            }

            const segObj = new Segment(engine.renderer, pStart, pEnd, false, `seg_${startName}${endName}`);
            segObj.setVisible(false);
            createdObjects.set(`seg_${startName}${endName}`, segObj);
            animations.push(new DrawLine(segObj, engine.ruler));
            break;
          }

          case 'arc': {
            const centerName = typeof act.center === 'string' ? act.center : 'A';
            const rawIntersects = act.intersects;
            let intersectsTarget: string | string[] | undefined;

            if (rawIntersects === 'previous' || (typeof rawIntersects === 'string' && rawIntersects.startsWith('arc'))) {
              intersectsTarget = arcKeys[arcKeys.length - 1];
            } else {
              intersectsTarget = rawIntersects;
            }

            const intersectionPoints = act.intersectionPoints || (act.intersectionPoint ? [act.intersectionPoint] : []);

            const arcResults = solver.solveArcWithIntersection({
              centerName,
              radiusSpec: act.radius === 'default' ? 120 : act.radius,
              intersectsTarget,
              intersectionPointName: act.intersectionPoint,
              intersectionPoints,
              side: act.side,
            });

            arcResults.forEach((arcRes, aIdx) => {
              const currentArcKey = `arc_${centerName}_${arcKeys.length + 1}_${aIdx}`;
              arcKeys.push(currentArcKey);

              const arcObj = new Arc(
                engine.renderer,
                arcRes.center,
                arcRes.radius,
                arcRes.startAngle,
                arcRes.endAngle,
                true,
                false,
                currentArcKey
              );
              arcObj.setVisible(false);

              animations.push(new DrawArc(arcObj, engine.compass));

              const ptName = arcRes.intersectionName || intersectionPoints[aIdx] || (aIdx === 0 ? act.intersectionPoint : undefined);
              if (ptName && arcRes.intersection) {
                let interObj = createdObjects.get(ptName);
                if (!interObj) {
                  interObj = new Point(
                    engine.renderer,
                    arcRes.intersection.x,
                    arcRes.intersection.y,
                    ptName,
                    `pt_${ptName}`
                  );
                  interObj.setVisible(false);
                  createdObjects.set(ptName, interObj);
                  animations.push(new DrawPoint(interObj));
                }
              }
            });
            break;
          }

          case 'resultAngle':
          case 'angleMark': {
            const vertexP = solver.getPoint(act.vertex || 'A');
            const arm1P = solver.getPoint(act.arm1 || 'B');
            const arm2P = solver.getPoint(act.arm2 || 'C');

            const angleMark = new Angle(engine.renderer, vertexP, arm1P, arm2P, act.label || '', false, `angle_result_${Math.random().toString(36).substring(2, 7)}`);
            angleMark.setVisible(false);
            animations.push(new DrawPoint(angleMark as any));
            break;
          }

          case 'circle': {
            const centerPos = Array.isArray(act.center) 
              ? { x: act.center[0], y: act.center[1] } 
              : solver.getPoint(act.center || 'O');
            const radius = act.radius || 100;
            const circle = new Circle(engine.renderer, centerPos, radius, false, `circle_${Math.random().toString(36).substring(2, 7)}`);
            circle.setVisible(false);
            animations.push(new DrawCircle(circle, act.tool === 'compass' ? engine.compass : undefined));
            break;
          }

          case 'point': {
            const pos = Array.isArray(act.pos) 
              ? { x: act.pos[0], y: act.pos[1] } 
              : solver.getPoint(act.name || 'A');
            const ptObj = new Point(engine.renderer, pos.x, pos.y, act.text || act.name || '', `pt_${act.name || Math.random().toString(36).substring(2, 7)}`);
            ptObj.setVisible(false);
            animations.push(new DrawPoint(ptObj));
            break;
          }

          case 'label': {
            const pos = Array.isArray(act.pos) ? { x: act.pos[0], y: act.pos[1] } : { x: 300, y: 300 };
            const labelObj = new Label(engine.renderer, pos, act.text || '', `lbl_${Math.random().toString(36).substring(2, 7)}`);
            labelObj.setVisible(false);
            animations.push(new Fade(labelObj, true));
            break;
          }
        }
      });

      const stepObj: ConstructionStep = {
        id: `step_${tbStep.step || idx + 1}`,
        title: tbStep.title || `Step ${idx + 1}`,
        description: tbStep.narration || '',
        narration: tbStep.narration || tbStep.title,
        duration: 1800,
        animations,
      };

      if (tbStep.voiceoverAudioUrl) {
        (stepObj as any).voiceoverAudioUrl = tbStep.voiceoverAudioUrl;
      }

      steps.push(stepObj);
    });

    return { steps, justification: json.justification };
  }
}
